// src/pages/Amostras/AmostraPage.jsx
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import FiltrosPanel, { CAMPO } from '../../components/FiltrosPanel';
import PaginacaoFooter from '../../components/PaginacaoFooter';
import {
    Box,
    Typography,
    Stack,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Skeleton,
    Button,
    InputAdornment,
    Tooltip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Card,
    CardContent,
    CardActions,
    Chip,
    Badge,
    IconButton,
    CardActionArea,
    Fab,
    useMediaQuery,
    useTheme,
    alpha,
} from '@mui/material';
import * as Lucide from 'lucide-react';

import { SnackbarAlert } from '../../components/SnackbarAlert';
import { useBootstrap } from '../../context/BootstrapContext';
import { usePermissao } from '../../security/usePermissao';
import useExperimentosAtivos from '../../hooks/useExperimentosAtivos';

import {
    listarAmostras,
    criarAmostra,
    atualizarAmostra,
    criarAmostraMock,
    normalizarAmostrasPorDia,
    excluirAmostra,
    restaurarAmostra,
    adicionarComentarioAmostra,
    buscarAmostraDetalhes,
    StatusAmostra,
} from '../../infra/api/Amostras';
import { iniciarProcessamentoAmostra } from '../../infra/api/resultado';
import { getEstacaoConfig } from '../../infra/api/estacaoConfig';
import EficienciaResumo from '../../components/EficienciaResumo';
import {
    estadoResultado,
    LIMITE_PADRAO,
    BUFFER_PADRAO,
} from '../../utils/eficiencia';

import AmostraFormModal from './AnaliseFormModal';
import AnaliseAutoCreateModal from './AnaliseAutoCreateModal';
import AnaliseDetalhesModal from './AnaliseDetalhesModal';
import ComentariosAnaliseDialog from '../../components/ComentariosAnaliseDialog';
import AmostraMockModal from './AnaliseMockModal';
import EteRelatorioModal from './EteRelatorioModal';

const STATUS_OPTIONS = [
    { value: 'ALL', label: 'Todos' },
    { value: StatusAmostra.PENDENTE, label: 'Pendente' },
    { value: StatusAmostra.EM_PROCESSAMENTO, label: 'Em análise' },
    { value: StatusAmostra.ENCERRADA, label: 'Encerrada' },
];

const SORT_OPTIONS = [
    { value: 'createdAt,desc', label: 'Mais recentes' },
    { value: 'createdAt,asc', label: 'Mais antigas' },
    { value: 'identificacao,asc', label: 'Identificação (A-Z)' },
    { value: 'identificacao,desc', label: 'Identificação (Z-A)' },
];

// Motivos pré-definidos de exclusão (soft-delete). "Outro" exige a observação.
const MOTIVOS_EXCLUSAO = [
    'Duplicada',
    'Criada por engano',
    'Teste',
    'Dados inválidos/insuficientes',
    'Dispositivos incorretos',
    'Solicitação do cliente',
    'Outro',
];

const toIsoOrNull = (v) => {
    if (!v) return null;
    if (typeof v === 'string') return v;
    if (v instanceof Date) return v.toISOString();
    return String(v);
};

const todayIsoDate = () => {
    const now = new Date();
    const tzOffset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - tzOffset).toISOString().slice(0, 10);
};

const buildFilterPayload = ({
    identificacao,
    status,
    torreId,
    numeroSerieTorre,
    createdFrom,
    createdTo,
}) => {
    const filter = {};
    if (identificacao?.trim()) filter.identificacao = identificacao.trim();
    if (status && status !== 'ALL') filter.status = status;
    if (torreId?.trim()) filter.torreId = torreId.trim();
    if (numeroSerieTorre?.trim())
        filter.numeroSerieTorre = numeroSerieTorre.trim();
    const fromIso = toIsoOrNull(createdFrom);
    const toIso = toIsoOrNull(createdTo);
    if (fromIso) filter.createdFrom = fromIso;
    if (toIso) filter.createdTo = toIso;
    return filter;
};

const getStatusColor = (status) => {
    switch (status) {
        case 'PENDENTE':
            return 'warning';
        case 'EM_PROCESSAMENTO':
            return 'info';
        case 'ENCERRADA':
            return 'success';
        case 'ERRO':
            return 'error';
        default:
            return 'default';
    }
};

// ── Fonte única da regra de estados da análise ──────────────────────────────────
// Agrupa os status por fase do ciclo de vida. podeCalcular / podeExcluir / o chip de
// atividade derivam DAQUI, pra as regras não divergirem entre si (e baterem com o backend:
// validarAmostraProntaParaFila = TERMINAL; guarda de exclusão bloqueia RODANDO/CALCULANDO).
const StatusGrupo = {
    RODANDO: ['EM_PROCESSAMENTO'],          // experimento em curso
    CALCULANDO: ['ENVIADO'],                // cálculo enfileirado / em curso
    TERMINAL: ['FINALIZADA', 'ENCERRADA'],  // experimentos encerrados (pronto p/ calcular)
};
const statusOf = (a) => String(a?.status || '').toUpperCase();

// "Em atividade" = experimento rodando OU cálculo em curso → nem calcula nem exclui.
const analiseEmAtividade = (a) =>
    StatusGrupo.RODANDO.includes(statusOf(a)) ||
    StatusGrupo.CALCULANDO.includes(statusOf(a));

// Calcular: só com experimentos encerrados (FINALIZADA/ENCERRADA) e sem resultado ainda.
// Some durante PENDENTE/EM_PROCESSAMENTO/ENVIADO. O botão reaparece se o resultado for
// excluído em Detalhes. O backend reforça (validarAmostraProntaParaFila).
const podeCalcular = (a) =>
    StatusGrupo.TERMINAL.includes(statusOf(a)) && !a?.temResultado;

// Excluir: bloqueado enquanto em atividade. PENDENTE (não iniciada), FINALIZADA, ENCERRADA
// e ERRO podem ser excluídas; o backend reforça (cobre também "calculando").
const podeExcluir = (a) => !analiseEmAtividade(a);

// Rótulo do chip de atividade no card (UX): explica por que o Calcular não está lá.
const estadoAtividadeLabel = (a) => {
    const s = statusOf(a);
    if (StatusGrupo.RODANDO.includes(s)) return 'Em análise';
    if (StatusGrupo.CALCULANDO.includes(s)) return 'Calculando…';
    return null;
};

// Estado do vinculo de dispositivos, para o chip do card. O laboratorista opera pelo
// celular e precisa perceber DE RELANCE que ainda falta encaixar tubo — por isso o chip
// e colorido (nao outline apagado) e PULSA quando esta incompleto E ainda da pra vincular.
// Em analise ja terminada nao pulsa: nao ha acao possivel, seria so ruido.
const vinculoInfo = (a, vinculadas, total, aoVivo = false) => {
    const st = String(a?.status || '').toUpperCase();
    const acionavel = st === 'PENDENTE' || st === 'EM_PROCESSAMENTO';
    const completo = total > 0 && vinculadas >= total;
    const nenhum = vinculadas === 0;
    return {
        completo,
        pulsar: !completo && acionavel,
        // Completo = estado de repouso: cinza discreto (so o icone de check).
        // Cor forte fica reservada para o que EXIGE acao (faltam dispositivos).
        cor: completo ? 'default' : nenhum ? 'error' : 'warning',
        titulo: completo
            ? 'Todos os dispositivos vinculados'
            : `Faltam ${total - vinculadas} de ${total} dispositivos — encaixe os tubos restantes` +
              (aoVivo
                  ? '. A tela atualiza sozinha; não precisa clicar em Atualizar.'
                  : ''),
    };
};

// Ritmo da recarga silenciosa, por FASE. Um intervalo unico nao serve para as
// duas esperas, que sao de ordens de grandeza diferentes:
//
//   CALCULANDO — o modelo roda em ~segundos. Aqui o WebSocket nao ajuda: ele
//     observa os DISPOSITIVOS, e durante o calculo nenhum deles muda de estado
//     (quem trabalha e o servidor). Sem um ritmo rapido, o card ficava parado ate
//     um minuto DEPOIS do resultado estar pronto — que era o sintoma relatado.
//
//   ENSAIO — as amostras rodam por horas. Checar de 3 em 3 segundos o dia inteiro
//     seria desperdicio puro, para um dado que muda uma vez.
//
// Com a pagina em repouso (so analises finalizadas) nao ha requisicao nenhuma.
const POLL_CALCULANDO_MS = 3000;
const POLL_ENSAIO_MS = 60000;

// Teto do ritmo rapido. Um calculo travado em PENDENTE ficaria pedindo de 3 em 3
// segundos enquanto a aba existisse; passado esse limite cai para o ritmo lento —
// o dado continua chegando, so mais devagar.
const MAX_TICKS_RAPIDOS = 40; // ~2 min

const countLinked = (torres) => {
    if (!Array.isArray(torres)) return 0;
    return torres.filter((t) => {
        const s = String(t?.status || '').toUpperCase();
        return [
            'VINCULADA',
            'EM_PROCESSAMENTO',
            'PROCESSANDO',
            'ENCERRADA',
            'FINALIZADA',
            'COMPLETED',
            'CONCLUIDO',
        ].includes(s);
    }).length;
};

// Editar: espelha a guarda do backend (AmostraService.atualizarAmostra), que so
// aceita PENDENTE, sem experimentos vinculados e sem nenhuma torre fora de
// PENDENTE. Ou seja, a partir do PRIMEIRO tubo encaixado ja nao ha nada
// editavel — o botao seria um 409 garantido, entao ele some.
const podeEditar = (a) =>
    statusOf(a) === 'PENDENTE' && countLinked(a?.torres) === 0;

// Prazo de validade do adiantamento. O vinculo real e gravado pelo backend no
// mesmo instante em que o aparelho cria o experimento, e a recarga silenciosa
// chega ~2s depois — 30s e folga larga. Passado o prazo sem o servidor
// confirmar, aquilo deixa de ser "ja ja chega" e vira DIVERGENCIA.
const JANELA_CONFIRMACAO_MS = 30000;

// Contagem de dispositivos vinculados, cruzando servidor com telemetria ao vivo.
//
// Quem grava o vinculo (torres.$.status = VINCULADA) e o backend Go, quando o
// proprio aparelho cria o experimento ao receber o tubo. O CRM so descobriria
// isso na proxima listagem — por isso o card ficava em 0/4 com o tubo ja dentro.
// O adiantamento cobre essa janela.
//
// Devolve tambem `semVinculo`: aparelhos transmitindo com experimento que o
// servidor CONTINUA marcando PENDENTE depois do prazo. Isso nao e atraso, e
// defeito — e aconteceu de verdade (2026-08-31: o backend alocou os tubos para
// outra analise). Na epoca o adiantamento otimista contou esses aparelhos como
// vinculados e exibiu 4/4 enquanto o servidor tinha 1/4, escondendo o problema
// pelo ensaio inteiro. Agora eles saem da conta e viram alerta no card.
//
// Travas que permanecem:
//  - so vale em analise PENDENTE/EM_PROCESSAMENTO. Numa analise encerrada o
//    aparelho pode ter sido reaproveitado por OUTRA analise, e o experimentId
//    seria de outro ensaio -> contagem falsa.
//  - nunca decrementa nem passa do total: o tempo real so pode ADIANTAR o que o
//    servidor vai confirmar, jamais contradize-lo.
const contarVinculadas = (a, experimentoPorSerial, vistoDesde, agora) => {
    const base = countLinked(a?.torres);
    const st = String(a?.status || '').toUpperCase();

    if (st !== 'PENDENTE' && st !== 'EM_PROCESSAMENTO') {
        return { vinculadas: base, semVinculo: 0 };
    }
    if (!Array.isArray(a?.torres) || !experimentoPorSerial) {
        return { vinculadas: base, semVinculo: 0 };
    }

    let adiantadas = 0;
    let semVinculo = 0;

    a.torres.forEach((t) => {
        if (String(t?.status || '').toUpperCase() !== 'PENDENTE') return;

        const serial = String(t?.numeroSerie || '').trim();
        if (!experimentoPorSerial[serial]) return;

        const desde = vistoDesde?.[serial];
        if (desde != null && agora - desde > JANELA_CONFIRMACAO_MS) {
            semVinculo += 1;
        } else {
            adiantadas += 1;
        }
    });

    return {
        vinculadas: Math.min(a.torres.length, base + adiantadas),
        semVinculo,
    };
};

const normalizeText = (value) => String(value || '').trim();

const extractProtocolosAnalises = (source) => {
    if (!source || !Array.isArray(source.protocolosAnalises)) return [];
    return source.protocolosAnalises
        .map((item) => {
            if (!item || typeof item !== 'object') return null;
            const protocoloId = normalizeText(item.protocoloId);
            const protocoloNome = normalizeText(item.protocoloNome);
            const analiseId = normalizeText(item.analiseId);
            const analiseNome = normalizeText(item.analiseNome);
            if (!protocoloId && !protocoloNome && !analiseId && !analiseNome) {
                return null;
            }
            return {
                protocoloId,
                protocoloNome,
                analiseId,
                analiseNome,
            };
        })
        .filter(Boolean);
};

const uniq = (values) => [...new Set(values.filter(Boolean))];

const resolveProtocoloLabel = (source) => {
    const refs = extractProtocolosAnalises(source);
    const nomes = uniq(refs.map((item) => item.protocoloNome));
    if (nomes.length) return nomes.join(' + ');
    const ids = uniq(refs.map((item) => item.protocoloId));
    if (ids.length) return ids.join(' + ');
    return 'Protocolo N/A';
};

const resolveAnaliseLabel = (source) => {
    const refs = extractProtocolosAnalises(source);
    const nomes = uniq(refs.map((item) => item.analiseNome));
    if (nomes.length) return nomes.join(' + ');
    const ids = uniq(refs.map((item) => item.analiseId));
    if (ids.length) return ids.join(' + ');
    return 'Análise N/A';
};

const AmostraPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { pode } = usePermissao();

    const [amostras, setAmostras] = useState([]);
    const [totalPages, setTotalPages] = useState(1);

    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sort, setSort] = useState('createdAt,desc');

    const [identificacao, setIdentificacao] = useState('');
    const [status, setStatus] = useState('ALL');
    const [numeroSerieTorre, setNumeroSerieTorre] = useState('');
    const [torreId, setTorreId] = useState('');
    const [createdFrom, setCreatedFrom] = useState('');
    const [createdTo, setCreatedTo] = useState('');

    const [openForm, setOpenForm] = useState(false);
    const [openAuto, setOpenAuto] = useState(false);
    const [editing, setEditing] = useState(null);
    const [openMock, setOpenMock] = useState(false);
    const [openNormalize, setOpenNormalize] = useState(false);
    const [normalizing, setNormalizing] = useState(false);
    const [normalizeLocLoading, setNormalizeLocLoading] = useState(false);
    const [normalizeLocMessage, setNormalizeLocMessage] = useState('');
    const [normalizeForm, setNormalizeForm] = useState({
        escopo: 'dia',
        dataReferencia: todayIsoDate(),
        dataInicio: todayIsoDate(),
        dataFim: todayIsoDate(),
        janelaMinutos: 90,
        minimoExperimentos: 1,
        statusExperimento: 'completed',
        temperaturaAmostra: '',
        latitude: '',
        longitude: '',
    });
    const [normalizePreview, setNormalizePreview] = useState(null);

    const [openEte, setOpenEte] = useState(false);

    const [pageSnackbar, setPageSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info',
    });

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteMotivo, setDeleteMotivo] = useState('');
    const [deleteObs, setDeleteObs] = useState('');

    // Limite regulatório (FEPAM) da estação — buscado UMA vez por página (nunca por card).
    const [estacao, setEstacao] = useState({
        limite: LIMITE_PADRAO,
        buffer: BUFFER_PADRAO,
    });

    // Lixeira (soft-delete): só ADMIN. Mostra as análises excluídas + restaurar.
    const [showDeleted, setShowDeleted] = useState(false);
    const [restoringId, setRestoringId] = useState(null);

    // Comentários da análise. Guarda o ID (não o objeto) para o diálogo sempre ler a
    // versão atual da lista — senão um poll silencioso atualizaria `amostras` e o
    // diálogo continuaria mostrando um retrato velho.
    const [comentariosId, setComentariosId] = useState(null);
    const amostraComentarios = useMemo(
        () => amostras.find((a) => (a?.id || a?._id) === comentariosId) || null,
        [amostras, comentariosId]
    );

    const [openDetalhes, setOpenDetalhes] = useState(false);
    const [detalhesTarget, setDetalhesTarget] = useState(null);
    const [detalhesLoading, setDetalhesLoading] = useState(false);
    const [detalhesData, setDetalhesData] = useState(null);
    const [detalhesError, setDetalhesError] = useState('');

    const { bootstrap } = useBootstrap();

    // Cliente SANEAMENTO: 1 protocolo com exatamente Afluente + Efluente => criação automática.
    const isSaneamentoClient = useMemo(() => {
        const protocolos = bootstrap?.protocolos || bootstrap?.protocols || [];
        if (protocolos.length !== 1) return false;
        const analises = protocolos[0]?.analises || protocolos[0]?.analysis || [];
        if (analises.length !== 2) return false;
        const nomes = analises.map((a) =>
            String(a?.name || a?.nome || '').toLowerCase()
        );
        return (
            nomes.some((n) => n.includes('afluente')) &&
            nomes.some((n) => n.includes('efluente'))
        );
    }, [bootstrap]);

    const filterPayload = useMemo(
        () => ({
            ...buildFilterPayload({
                identificacao,
                status,
                torreId,
                numeroSerieTorre,
                createdFrom,
                createdTo,
            }),
            apenasExcluidas: showDeleted || undefined,
        }),
        [
            identificacao,
            status,
            torreId,
            numeroSerieTorre,
            createdFrom,
            createdTo,
            showDeleted,
        ]
    );

    const visibleAmostras = useMemo(() => {
        if (!amostras) return [];
        if (pode('analise.verMocks')) return amostras;

        return amostras.filter((a) => {
            const iden = String(a?.identificacao || '').toUpperCase();
            return !iden.startsWith('MOCK-');
        });
    }, [amostras, pode]);

    // `silencioso`: recarga de segundo plano, disparada pelo tempo real. Nao
    // acende o skeleton (senao o grid inteiro pisca sozinho na cara do usuario)
    // nem mostra snackbar de erro — ninguem pediu essa requisicao, entao ela nao
    // pode assustar. Se falhar, o proximo gatilho tenta de novo.
    const loadAmostras = useCallback(
        async ({ silencioso = false } = {}) => {
            if (!silencioso) setLoading(true);
            try {
                const resp = await listarAmostras(
                    filterPayload,
                    page - 1,
                    itemsPerPage,
                    sort
                );
                setAmostras(resp?.content || []);
                setTotalPages(Math.max(1, resp?.totalPages || 1));
            } catch (e) {
                if (silencioso) return;
                setAmostras([]);
                setTotalPages(1);
                setPageSnackbar({
                    open: true,
                    message: 'Falha ao carregar as análises.',
                    severity: 'error',
                });
            } finally {
                if (!silencioso) setLoading(false);
            }
        },
        [filterPayload, page, itemsPerPage, sort]
    );

    useEffect(() => {
        loadAmostras();
    }, [loadAmostras]);

    // -------------------------------------------------------------------
    // TEMPO REAL — o card mentia nas DUAS pontas do ensaio.
    //
    // Inicio: criada a analise o chip fica em 0/4 ate alguem clicar em
    //   Atualizar. Quem vincula o dispositivo e o proprio aparelho, contra o
    //   backend Go; o CRM nunca ficava sabendo.
    // Fim: retirados os tubos o servidor JA finaliza a analise sozinho, mas a
    //   tela seguia em EM_PROCESSAMENTO, escondendo o botao Calcular.
    //
    // As duas transicoes tem sinais de natureza diferente, e por isso usam
    // mecanismos diferentes:
    //   inicio = PRESENCA (o experimentId aparece na telemetria). Um aparelho
    //     que acabou de iniciar esta publicando com certeza -> o WebSocket
    //     resolve, instantaneo e sem custar requisicao.
    //   fim = AUSENCIA (o aparelho para). Ausencia nao e observavel com
    //     confianca: "parou de enviar" e indistinguivel de "caiu a rede". Por
    //     isso o fim depende da recarga periodica; o WS so serve de atalho se
    //     por acaso mandar o frame ocioso.
    // -------------------------------------------------------------------

    // Enquanto o servidor ainda pode mudar o card sozinho, ficamos ao vivo.
    // PENDENTE = esperando encaixar · EM_PROCESSAMENTO = vai finalizar sozinha ·
    // ENVIADO = calculando, vai virar resultado. Fora disso nada muda sem acao
    // do usuario -> nem socket, nem poll.
    const faseAoVivo = useMemo(() => {
        let calculando = false;
        let ensaio = false;

        visibleAmostras.forEach((a) => {
            // Analise recem-criada esperando tubo: o vinculo chega pelo
            // WebSocket, e nesse ponto ainda nao existe resultado para
            // estadoResultado() enxergar.
            if (statusOf(a) === 'PENDENTE') {
                ensaio = true;
                return;
            }

            // Nas demais fases usa a MESMA regra que decide o rotulo do card.
            // Antes o portao olhava so o status da amostra, e por isso discordava
            // da tela: ao calcular uma analise ja FINALIZADA o backend a mantem
            // FINALIZADA (so marca ENVIADO quando ela ainda nao estava), entao o
            // card dizia "Calculando resultado..." — vindo do resultado PENDENTE —
            // enquanto o portao via FINALIZADA e desligava socket e poll.
            const fase = estadoResultado(a).key;
            if (fase === 'calculando') calculando = true;
            else if (fase === 'em-andamento') ensaio = true;
        });

        return { calculando, ensaio, ativo: calculando || ensaio };
    }, [visibleAmostras]);

    const aoVivoAtivo = faseAoVivo.ativo;
    const faseCalculando = faseAoVivo.calculando;

    const { experimentoPorSerial, connected } = useExperimentosAtivos({
        enabled: aoVivoAtivo,
    });

    // Quando cada serial foi visto transmitindo pela primeira vez. Fica em ref
    // (nao em estado) porque so serve para medir prazo — quem provoca o
    // reencontro com a tela e o recheck abaixo, uma vez, no fim da janela.
    const vistoDesdeRef = useRef({});
    const [, forcarRecheck] = useState(0);

    useEffect(() => {
        const agora = Date.now();
        const anterior = vistoDesdeRef.current;
        const atual = {};
        Object.keys(experimentoPorSerial).forEach((serial) => {
            atual[serial] = anterior[serial] ?? agora;
        });
        vistoDesdeRef.current = atual;

        if (!Object.keys(atual).length) return undefined;

        // Reavalia quando a janela expira: e o instante em que o card para de
        // adiantar e passa a sinalizar que ha aparelho rodando sem vinculo.
        const t = setTimeout(
            () => forcarRecheck((n) => n + 1),
            JANELA_CONFIRMACAO_MS + 1000
        );
        return () => clearTimeout(t);
    }, [experimentoPorSerial]);

    // Ref para os gatilhos nao dependerem da identidade de loadAmostras: ela
    // muda a cada troca de filtro/pagina, e sem isso o poll seria reiniciado
    // (ou disparado) a cada uma dessas mudancas.
    const loadRef = useRef(loadAmostras);
    loadRef.current = loadAmostras;

    // Gatilho 1 (rapido): o mapa serial -> experimentId mudou, ou seja algum
    // aparelho iniciou ou parou. Debounce porque os 4 tubos sao encaixados em
    // sequencia e sem ele isso viraria 4 requisicoes seguidas.
    const mapaAnteriorRef = useRef(null);
    useEffect(() => {
        const mudou =
            mapaAnteriorRef.current !== null &&
            mapaAnteriorRef.current !== experimentoPorSerial;
        mapaAnteriorRef.current = experimentoPorSerial;

        if (!mudou || !aoVivoAtivo) return undefined;

        const t = setTimeout(
            () => loadRef.current({ silencioso: true }),
            2000
        );
        return () => clearTimeout(t);
    }, [experimentoPorSerial, aoVivoAtivo]);

    // Gatilho 2 (garantia de corretude): o fim do ensaio e uma ausencia de
    // sinal, entao NAO pode depender do WebSocket. Enquanto ha analise ao vivo,
    // uma recarga silenciosa periodica traz a verdade do servidor — e cobre de
    // quebra o aparelho que simplesmente sumiu da rede.
    useEffect(() => {
        if (!aoVivoAtivo) return undefined;

        let ticksRapidos = 0;
        let id = null;

        const agendar = (intervalo) => {
            if (id) clearInterval(id);
            id = setInterval(() => {
                if (document.visibilityState === 'hidden') return;
                loadRef.current({ silencioso: true });

                // Desacelera se o calculo passar do razoavel (ver MAX_TICKS_RAPIDOS).
                if (intervalo === POLL_CALCULANDO_MS) {
                    ticksRapidos += 1;
                    if (ticksRapidos >= MAX_TICKS_RAPIDOS) agendar(POLL_ENSAIO_MS);
                }
            }, intervalo);
        };

        agendar(faseCalculando ? POLL_CALCULANDO_MS : POLL_ENSAIO_MS);
        return () => {
            if (id) clearInterval(id);
        };
        // Depende dos BOOLEANOS, nao do objeto: faseAoVivo ganha identidade nova a
        // cada recarga silenciosa, e isso reiniciaria o timer a cada tick.
    }, [aoVivoAtivo, faseCalculando]);

    // Limite regulatorio da estacao: UMA busca por pagina (nao por card).
    // Falha silenciosa -> cai nos defaults (80% / 5pp), sem erro visivel ao usuario.
    useEffect(() => {
        let cancelado = false;
        getEstacaoConfig()
            .then((cfg) => {
                if (cancelado || !cfg) return;
                setEstacao({
                    limite: cfg.limiteRegulatorio ?? LIMITE_PADRAO,
                    buffer: cfg.bufferAtencao ?? BUFFER_PADRAO,
                });
            })
            .catch(() => {});
        return () => {
            cancelado = true;
        };
    }, []);

    useEffect(() => {
        setPage(1);
    }, [filterPayload, sort, itemsPerPage]);

    // Mostra o bloco de eficiencia so no SANEAMENTO (a classificacao afluente/efluente do
    // backend e especifica desse cliente) E so quando o backend ja envia os campos novos —
    // se o backend antigo ainda estiver no ar, cai no bloco legado em vez de "sem resultado".
    const mostrarEficiencia = useCallback(
        (a) => {
            if (!isSaneamentoClient) return false;
            const backendAntigo =
                a?.temResultado === true && a?.resultadoStatus === undefined;
            return !backendAntigo;
        },
        [isSaneamentoClient]
    );

    const handleOpenCreate = () => {
        // Cliente SANEAMENTO => fluxo automático (1 clique). Demais => modal manual.
        if (isSaneamentoClient) {
            setOpenAuto(true);
            return;
        }
        setEditing(null);
        setOpenForm(true);
    };

    const handleCreateAuto = async (payload) => {
        if (!pode('dados.alterar')) {
            throw new Error('Modo Visitante: criação bloqueada.');
        }
        await criarAmostra(payload);
        setPageSnackbar({
            open: true,
            message: 'Análise criada com sucesso.',
            severity: 'success',
        });
        loadAmostras();
    };

    const handleOpenMock = () => {
        setOpenMock(true);
    };

    const handleOpenEdit = (amostra, e) => {
        e?.stopPropagation();
        setEditing(amostra);
        setOpenForm(true);
    };

    const handleCloseForm = () => {
        setOpenForm(false);
        setEditing(null);
    };

    const handleCloseMock = () => {
        setOpenMock(false);
    };

    const handleSubmitForm = async (values) => {
        if (!pode('dados.alterar')) {
            setPageSnackbar({
                open: true,
                message: 'Modo Visitante: Ação de criar/editar bloqueada.',
                severity: 'info',
            });
            return;
        }

        try {
            if (!editing) {
                await criarAmostra(values);
                setPageSnackbar({
                    open: true,
                    message: 'Análise criada com sucesso.',
                    severity: 'success',
                });
            } else {
                const id = editing?.id || editing?._id;
                await atualizarAmostra(id, values);
                setPageSnackbar({
                    open: true,
                    message: 'Análise atualizada com sucesso.',
                    severity: 'success',
                });
            }

            handleCloseForm();
            loadAmostras();
        } catch (e) {
            setPageSnackbar({
                open: true,
                message: e?.message || 'Falha ao salvar análise.',
                severity: 'error',
            });
        }
    };

    const handleSubmitMock = async (payload) => {
        if (!pode('dados.alterar')) {
            setPageSnackbar({
                open: true,
                message: 'Modo Visitante: Criação de mock bloqueada.',
                severity: 'info',
            });
            return;
        }

        try {
            await criarAmostraMock(payload);
            setPageSnackbar({
                open: true,
                message: 'Análise mock criada com sucesso.',
                severity: 'success',
            });
            setOpenMock(false);
            loadAmostras();
        } catch (e) {
            setPageSnackbar({
                open: true,
                message: e?.message || 'Falha ao criar análise mock.',
                severity: 'error',
            });
            throw e;
        }
    };

    const prefillNormalizeLocationFromGps = useCallback((force = false) => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            setNormalizeLocMessage('GPS indisponivel neste navegador.');
            return;
        }

        setNormalizeLocLoading(true);
        setNormalizeLocMessage('');

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = Number(pos.coords.latitude).toFixed(6);
                const lon = Number(pos.coords.longitude).toFixed(6);

                setNormalizeForm((prev) => {
                    const hasLat = String(prev.latitude || '').trim() !== '';
                    const hasLon = String(prev.longitude || '').trim() !== '';
                    if (!force && hasLat && hasLon) {
                        return prev;
                    }
                    return {
                        ...prev,
                        latitude: String(lat),
                        longitude: String(lon),
                    };
                });
                setNormalizeLocMessage(`GPS capturado: ${lat}, ${lon}`);
                setNormalizeLocLoading(false);
            },
            () => {
                setNormalizeLocMessage(
                    'Nao foi possivel capturar GPS (permissao negada ou indisponivel).'
                );
                setNormalizeLocLoading(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
            }
        );
    }, []);

    const handleOpenNormalize = () => {
        setNormalizeForm({
            escopo: 'dia',
            dataReferencia: todayIsoDate(),
            dataInicio: todayIsoDate(),
            dataFim: todayIsoDate(),
            janelaMinutos: 90,
            minimoExperimentos: 1,
            statusExperimento: 'completed',
            temperaturaAmostra: '',
            latitude: '',
            longitude: '',
        });
        setNormalizeLocMessage('');
        setNormalizePreview(null);
        setOpenNormalize(true);
        prefillNormalizeLocationFromGps(false);
    };

    const handleCloseNormalize = () => {
        if (normalizing) return;
        setOpenNormalize(false);
    };

    const handleSubmitNormalize = async (dryRunMode = true) => {
        if (!pode('dados.alterar')) {
            setPageSnackbar({
                open: true,
                message: 'Modo Visitante: normalizacao bloqueada.',
                severity: 'info',
            });
            return;
        }

        setNormalizing(true);
        try {
            const lat = String(normalizeForm.latitude || '').trim();
            const lon = String(normalizeForm.longitude || '').trim();
            const temp = String(normalizeForm.temperaturaAmostra || '').trim();

            const payload = {
                janelaMinutos: Number(normalizeForm.janelaMinutos || 90),
                minimoExperimentos: Number(
                    normalizeForm.minimoExperimentos || 2
                ),
                statusExperimento:
                    normalizeForm.statusExperimento || 'completed',
                dryRun: !!dryRunMode,
                ...(temp !== '' ? { temperaturaAmostra: Number(temp) } : {}),
                ...(lat !== '' && lon !== ''
                    ? {
                          localizacao: {
                              latitude: Number(lat),
                              longitude: Number(lon),
                          },
                      }
                    : {}),
            };

            if (normalizeForm.escopo === 'todos') {
                payload.todos = true;
            } else if (normalizeForm.escopo === 'periodo') {
                payload.dataInicio = normalizeForm.dataInicio;
                payload.dataFim = normalizeForm.dataFim;
            } else {
                payload.dataReferencia = normalizeForm.dataReferencia;
            }

            const result = await normalizarAmostrasPorDia(payload);
            setNormalizePreview(result);
            const criados = Number(result?.gruposCriados || 0);
            const detectados = Number(result?.gruposDetectados || 0);
            const ignorados = Number(result?.gruposIgnorados || 0);
            const prefix = payload.dryRun
                ? 'Simulacao concluida'
                : 'Normalizacao concluida';

            setPageSnackbar({
                open: true,
                message: `${prefix}: ${criados} grupo(s) apto(s)/criado(s), ${detectados} detectado(s), ${ignorados} ignorado(s).`,
                severity: 'success',
            });

            if (!payload.dryRun) {
                await loadAmostras();
            }
        } catch (e) {
            setPageSnackbar({
                open: true,
                message: e?.message || 'Falha ao normalizar análises.',
                severity: 'error',
            });
        } finally {
            setNormalizing(false);
        }
    };

    const clearFilters = () => {
        setIdentificacao('');
        setStatus('ALL');
        setNumeroSerieTorre('');
        setTorreId('');
        setCreatedFrom('');
        setCreatedTo('');
    };

    const filtrosAtivos = useMemo(() => {
        return (
            (identificacao.trim() ? 1 : 0) +
            (status !== 'ALL' ? 1 : 0) +
            (numeroSerieTorre.trim() ? 1 : 0) +
            (torreId.trim() ? 1 : 0) +
            (createdFrom ? 1 : 0) +
            (createdTo ? 1 : 0)
        );
    }, [
        identificacao,
        status,
        numeroSerieTorre,
        torreId,
        createdFrom,
        createdTo,
    ]);

    const canRunNormalize = useMemo(() => {
        if (normalizeForm.escopo === 'todos') return true;
        if (normalizeForm.escopo === 'periodo') {
            return !!normalizeForm.dataInicio && !!normalizeForm.dataFim;
        }
        return !!normalizeForm.dataReferencia;
    }, [
        normalizeForm.escopo,
        normalizeForm.dataReferencia,
        normalizeForm.dataInicio,
        normalizeForm.dataFim,
    ]);

    const handleAbrirComentarios = (amostra, e) => {
        e?.stopPropagation();
        setComentariosId(amostra?.id || amostra?._id || null);
    };

    const handleAdicionarComentario = useCallback(
        async (texto) => {
            if (!comentariosId) return;
            const salva = await adicionarComentarioAmostra(comentariosId, texto);

            // Merge SÓ do campo `comentarios`, nunca do objeto inteiro: a resposta vem
            // da entidade salva, sem os @Transient de enriquecimento (eficiência,
            // temResultado, resultadoStatus). Trocar o objeto apagaria o bloco de
            // eficiência do card até a próxima recarga.
            //
            // E merge em memória em vez de loadAmostras(): um reload aqui brigaria com
            // o poll silencioso e com o overlay de vínculo ao vivo.
            setAmostras((prev) =>
                prev.map((a) =>
                    (a?.id || a?._id) === comentariosId
                        ? { ...a, comentarios: salva?.comentarios ?? a?.comentarios }
                        : a
                )
            );
        },
        [comentariosId]
    );

    const handleAskDelete = (amostra, e) => {
        e?.stopPropagation();
        setDeleteTarget(amostra);
        setDeleteMotivo('');
        setDeleteObs('');
        setConfirmDeleteOpen(true);
    };

    const handleCloseDelete = () => {
        if (deleting) return;
        setConfirmDeleteOpen(false);
        setDeleteTarget(null);
        setDeleteMotivo('');
        setDeleteObs('');
    };

    // Motivo é obrigatório; "Outro" exige a observação. O motivo enviado ao backend
    // combina o preset + a observação livre.
    const deleteMotivoValido =
        !!deleteMotivo && (deleteMotivo !== 'Outro' || !!deleteObs.trim());

    const composeMotivo = () => {
        const obs = deleteObs.trim();
        if (deleteMotivo === 'Outro') return obs;
        return obs ? `${deleteMotivo} — ${obs}` : deleteMotivo;
    };

    const handleConfirmDelete = async () => {
        if (!pode('dados.alterar')) {
            setPageSnackbar({
                open: true,
                message: 'Modo Visitante: Exclusão bloqueada.',
                severity: 'info',
            });
            handleCloseDelete();
            return;
        }

        const id = deleteTarget?.id || deleteTarget?._id;
        if (!id || !deleteMotivoValido) return;

        setDeleting(true);
        try {
            await excluirAmostra(id, composeMotivo());
            setPageSnackbar({
                open: true,
                message: 'Análise movida para a lixeira.',
                severity: 'success',
            });
            setConfirmDeleteOpen(false);
            setDeleteTarget(null);
            setDeleteMotivo('');
            setDeleteObs('');
            await loadAmostras();
        } catch (e) {
            setPageSnackbar({
                open: true,
                message: e?.message || 'Falha ao excluir análise.',
                severity: 'error',
            });
        } finally {
            setDeleting(false);
        }
    };

    const handleRestore = async (amostra, e) => {
        e?.stopPropagation();
        const id = amostra?.id || amostra?._id;
        if (!id) return;
        setRestoringId(id);
        try {
            await restaurarAmostra(id);
            setPageSnackbar({
                open: true,
                message: 'Análise restaurada com sucesso.',
                severity: 'success',
            });
            await loadAmostras();
        } catch (err) {
            setPageSnackbar({
                open: true,
                message: err?.message || 'Falha ao restaurar análise.',
                severity: 'error',
            });
        } finally {
            setRestoringId(null);
        }
    };

    const handleCloseDetalhes = () => {
        if (detalhesLoading) return;
        setOpenDetalhes(false);
        setDetalhesTarget(null);
        setDetalhesData(null);
        setDetalhesError('');
    };

    const handleOpenDetalhes = async (amostra) => {
        const id = amostra?.id || amostra?._id;
        if (!id) return;

        setOpenDetalhes(true);
        setDetalhesTarget(amostra);
        setDetalhesLoading(true);
        setDetalhesData(null);
        setDetalhesError('');

        try {
            const data = await buscarAmostraDetalhes(id);
            setDetalhesData(data);
        } catch (e) {
            setDetalhesError(e?.message || 'Falha ao buscar detalhes.');
        } finally {
            setDetalhesLoading(false);
        }
    };

    const handleCalcularFromCard = async (a, e) => {
        e?.stopPropagation();
        if (!pode('dados.alterar')) {
            setPageSnackbar({
                open: true,
                message: 'Modo Visitante: cálculo bloqueado.',
                severity: 'info',
            });
            return;
        }
        const id = a?.id || a?._id;
        if (!id) return;
        try {
            await iniciarProcessamentoAmostra(id);
            setPageSnackbar({
                open: true,
                message:
                    'Calculando... o resultado aparece nos Detalhes em instantes.',
                severity: 'info',
            });
            await loadAmostras();
        } catch (err) {
            setPageSnackbar({
                open: true,
                message: err?.message || 'Falha ao calcular.',
                severity: 'error',
            });
        }
    };

    return (
        <Box
            component="main"
            sx={{
                p: { xs: 1.5, md: 2 },
                // Espaco extra embaixo no mobile: os FABs flutuam sobre o conteudo.
                pb: { xs: 12, sm: 1.5, md: 2 },
                width: '100%',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
                minHeight: '100%',
            }}
        >
            {/* Titulo da pagina e nome do cliente saem daqui: a navbar ja os exibe.
                As acoes ficam alinhadas a direita (no mobile viram FAB flutuante). */}
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="flex-end"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={2}
                sx={{ mb: 2 }}
            >
                {/* So aparece quando ha ensaio ao vivo. E o que avisa o
                    laboratorista leigo de que ele PODE esperar — sem isso ele
                    continua sem saber se a tela atualiza sozinha. */}
                {aoVivoAtivo && (
                    <Tooltip
                        title={
                            connected
                                ? 'A tela acompanha sozinha o encaixe e a retirada dos tubos — não precisa clicar em Atualizar.'
                                : 'Reconectando ao tempo real… a tela continua se atualizando sozinha, só com um pequeno atraso.'
                        }
                    >
                        <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            sx={{
                                mr: 'auto',
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: connected
                                    ? 'success.light'
                                    : 'divider',
                                bgcolor: connected
                                    ? (t) => alpha(t.palette.success.main, 0.08)
                                    : 'transparent',
                            }}
                        >
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: connected
                                        ? 'success.main'
                                        : 'grey.500',
                                }}
                            />
                            <Typography
                                variant="caption"
                                fontWeight={600}
                                color={
                                    connected
                                        ? 'success.main'
                                        : 'text.secondary'
                                }
                            >
                                {connected ? 'Tempo real' : 'Reconectando…'}
                            </Typography>
                        </Stack>
                    </Tooltip>
                )}

                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        width: isMobile ? '100%' : 'auto',
                        flexWrap: 'wrap',
                        gap: 1,
                    }}
                >
                    {/* <Tooltip title="Gerar Relatório ETE (Entrada x Saída)">
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={!isMobile && <Lucide.FileType />}
                            onClick={() => setOpenEte(true)}
                            disabled={
                                loading || (visibleAmostras?.length || 0) < 2
                            }
                            sx={{ whiteSpace: 'nowrap', borderRadius: 2 }}
                        >
                            {isMobile ? 'Relatório' : 'Relatório ETE'}
                        </Button>
                    </Tooltip> */}

                    {/* No mobile estes dois viram FAB flutuante (canto inferior direito),
                        para o laboratorista alcancar com o polegar usando so uma mao. */}
                    {!isMobile && (
                        <Tooltip title="Atualizar lista">
                            <Button
                                variant="outlined"
                                startIcon={<Lucide.RefreshCw />}
                                onClick={() => loadAmostras()}
                                disabled={loading}
                                size="small"
                                sx={{ borderRadius: 2 }}
                            >
                                Atualizar
                            </Button>
                        </Tooltip>
                    )}

                    {pode('analise.normalizarDia') && (
                        <Button
                            variant="outlined"
                            startIcon={!isMobile && <Lucide.Calendar />}
                            onClick={handleOpenNormalize}
                            color="info"
                            size="small"
                            sx={{
                                minWidth: isMobile ? 'auto' : 140,
                                whiteSpace: 'nowrap',
                                borderRadius: 2,
                            }}
                        >
                            {isMobile ? 'Normalizar' : 'Normalizar Dia'}
                        </Button>
                    )}

                    {pode('analise.criarMock') && (
                        <Button
                            variant="outlined"
                            startIcon={!isMobile && <Lucide.FlaskConical />}
                            onClick={handleOpenMock}
                            color="secondary"
                            size="small"
                            sx={{
                                minWidth: isMobile ? 'auto' : 100,
                                whiteSpace: 'nowrap',
                                borderRadius: 2,
                            }}
                        >
                            {isMobile ? 'Mock' : 'Criar Mock'}
                        </Button>
                    )}

                    {pode('lixeira.ver') && (
                        <Tooltip
                            title={
                                showDeleted
                                    ? 'Voltar às análises ativas'
                                    : 'Ver análises excluídas (Lixeira)'
                            }
                        >
                            <Button
                                variant={showDeleted ? 'contained' : 'outlined'}
                                color={showDeleted ? 'error' : 'inherit'}
                                startIcon={!isMobile && <Lucide.Trash2 />}
                                onClick={() => setShowDeleted((v) => !v)}
                                size="small"
                                sx={{
                                    minWidth: isMobile ? 'auto' : 100,
                                    whiteSpace: 'nowrap',
                                    borderRadius: 2,
                                }}
                            >
                                {isMobile ? 'Lixeira' : 'Lixeira'}
                            </Button>
                        </Tooltip>
                    )}

                    {!showDeleted && !isMobile && (
                        <Button
                            variant="contained"
                            startIcon={<Lucide.Plus />}
                            onClick={handleOpenCreate}
                            size="small"
                            sx={{
                                minWidth: 120,
                                whiteSpace: 'nowrap',
                                borderRadius: 2,
                                fontWeight: 'bold',
                            }}
                        >
                            Nova Análise
                        </Button>
                    )}
                </Stack>
            </Stack>

            <FiltrosPanel
                titulo="Filtros e Busca"
                ativos={filtrosAtivos}
                loading={loading}
                onLimpar={clearFilters}
                onBuscar={() => {
                    setPage(1);
                    loadAmostras();
                }}
            >
                        <Grid item {...CAMPO}>
                            <TextField
                                fullWidth
                                label="Identificação"
                                size="small"
                                value={identificacao}
                                onChange={(e) =>
                                    setIdentificacao(e.target.value)
                                }
                                disabled={loading}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lucide.QrCode
                                                size={18}
                                                color={theme.palette.action.active}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item {...CAMPO}>
                            <FormControl
                                fullWidth
                                size="small"
                                disabled={loading}
                            >
                                <InputLabel>Status</InputLabel>
                                <Select
                                    label="Status"
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    startAdornment={
                                        <InputAdornment
                                            position="start"
                                            sx={{ mr: 1 }}
                                        >
                                            <Lucide.FlaskConical
                                                size={18}
                                                color={theme.palette.action.active}
                                            />
                                        </InputAdornment>
                                    }
                                >
                                    {STATUS_OPTIONS.map((opt) => (
                                        <MenuItem
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item {...CAMPO}>
                            <FormControl
                                fullWidth
                                size="small"
                                disabled={loading}
                            >
                                <InputLabel>Ordenação</InputLabel>
                                <Select
                                    label="Ordenação"
                                    value={sort}
                                    onChange={(e) => setSort(e.target.value)}
                                >
                                    {SORT_OPTIONS.map((opt) => (
                                        <MenuItem
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item {...CAMPO}>
                            <TextField
                                fullWidth
                                label="Serial Dispositivo"
                                size="small"
                                value={numeroSerieTorre}
                                onChange={(e) =>
                                    setNumeroSerieTorre(e.target.value)
                                }
                                disabled={loading}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lucide.Hash
                                                size={18}
                                                color={theme.palette.action.active}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item {...CAMPO} xs={6}>
                            <TextField
                                fullWidth
                                label="De (Data)"
                                size="small"
                                value={createdFrom}
                                onChange={(e) => setCreatedFrom(e.target.value)}
                                disabled={loading}
                                placeholder="aaaa-mm-dd"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lucide.Calendar
                                                size={18}
                                                color={theme.palette.action.active}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item {...CAMPO} xs={6}>
                            <TextField
                                fullWidth
                                label="Até (Data)"
                                size="small"
                                value={createdTo}
                                onChange={(e) => setCreatedTo(e.target.value)}
                                disabled={loading}
                                placeholder="aaaa-mm-dd"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lucide.Calendar
                                                size={18}
                                                color={theme.palette.action.active}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

            </FiltrosPanel>

            <Grid container spacing={2}>
                {loading ? (
                    Array.from({ length: itemsPerPage }).map((_, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent>
                                    <Skeleton
                                        variant="text"
                                        height={40}
                                        width="60%"
                                    />
                                    <Skeleton
                                        variant="text"
                                        height={20}
                                        width="40%"
                                    />
                                    <Skeleton
                                        variant="rectangular"
                                        height={60}
                                        sx={{ mt: 2, borderRadius: 1 }}
                                    />
                                </CardContent>
                                <CardActions>
                                    <Skeleton
                                        variant="circular"
                                        width={30}
                                        height={30}
                                    />
                                    <Skeleton
                                        variant="circular"
                                        width={30}
                                        height={30}
                                    />
                                </CardActions>
                            </Card>
                        </Grid>
                    ))
                ) : (visibleAmostras?.length || 0) === 0 ? (
                    <Grid item xs={12}>
                        <Paper
                            sx={{
                                p: 6,
                                borderRadius: 3,
                                textAlign: 'center',
                                bgcolor: 'background.default',
                                border: '1px dashed',
                                borderColor: 'divider',
                            }}
                        >
                            <Lucide.FlaskConical
                                size={48}
                                color={theme.palette.text.disabled}
                                style={{ marginBottom: 16 }}
                            />
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                color="text.secondary"
                            >
                                {showDeleted
                                    ? 'A lixeira está vazia'
                                    : 'Nenhuma análise encontrada'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {showDeleted
                                    ? 'Nenhuma análise excluída.'
                                    : 'Tente ajustar os filtros ou crie uma nova análise.'}
                            </Typography>
                        </Paper>
                    </Grid>
                ) : (
                    visibleAmostras.map((a) => {
                        const key = a?.id || a?._id || a?.identificacao;
                        const stColor = getStatusColor(a?.status);

                        const totalTorres = Array.isArray(a?.torres)
                            ? a.torres.length
                            : 0;
                        const { vinculadas: vinculadasTorres, semVinculo } =
                            contarVinculadas(
                                a,
                                experimentoPorSerial,
                                vistoDesdeRef.current,
                                Date.now()
                            );
                        const vinculo = vinculoInfo(
                            a,
                            vinculadasTorres,
                            totalTorres,
                            aoVivoAtivo
                        );

                        const createdDate = a?.createdAt
                            ? new Date(a.createdAt).toLocaleDateString('pt-BR')
                            : '—';

                        return (
                            <Grid item xs={12} sm={6} md={4} key={key}>
                                <Card
                                    elevation={0}
                                    variant="outlined"
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: 2,
                                        transition:
                                            'border-color 0.2s, box-shadow 0.2s',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            boxShadow: theme.shadows[4],
                                        },
                                    }}
                                >
                                    <CardActionArea
                                        onClick={() => handleOpenDetalhes(a)}
                                        sx={{
                                            flexGrow: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <CardContent sx={{ width: '100%' }}>
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="flex-start"
                                                spacing={1}
                                                mb={2}
                                            >
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight={700}
                                                        noWrap
                                                        title={a?.identificacao}
                                                    >
                                                        {a?.identificacao ||
                                                            'Sem ID'}
                                                    </Typography>

                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        spacing={0.5}
                                                        mt={0.5}
                                                    >
                                                        <Lucide.Clock
                                                            size={14}
                                                            color={theme.palette.text.secondary}
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {createdDate}
                                                        </Typography>
                                                    </Stack>

                                                    <Tooltip
                                                        title={vinculo.titulo}
                                                    >
                                                        <Chip
                                                            icon={
                                                                vinculo.completo ? (
                                                                    <Lucide.CircleCheck
                                                                        size={14}
                                                                    />
                                                                ) : (
                                                                    <Lucide.CircleAlert
                                                                        size={14}
                                                                    />
                                                                )
                                                            }
                                                            label={`${vinculadasTorres}/${totalTorres} dispositivos`}
                                                            size="small"
                                                            color={vinculo.cor}
                                                            variant={
                                                                vinculo.completo
                                                                    ? 'outlined'
                                                                    : 'filled'
                                                            }
                                                            sx={{
                                                                mt: 0.75,
                                                                ...(vinculo.completo && {
                                                                    color: 'text.secondary',
                                                                    borderColor:
                                                                        'divider',
                                                                    fontWeight: 600,
                                                                }),
                                                                height: {
                                                                    xs: 26,
                                                                    sm: 22,
                                                                },
                                                                fontSize: {
                                                                    xs: '0.75rem',
                                                                    sm: '0.7rem',
                                                                },
                                                                fontWeight: 700,
                                                                '& .MuiChip-icon':
                                                                    {
                                                                        ml: 0.75,
                                                                        color: 'inherit',
                                                                    },
                                                                // Pulso lento: chama atencao sem virar estrobo.
                                                                ...(vinculo.pulsar && {
                                                                    animation:
                                                                        'pulsoVinculo 1.9s ease-in-out infinite',
                                                                    '@keyframes pulsoVinculo':
                                                                        {
                                                                            '0%, 100%':
                                                                                {
                                                                                    opacity: 1,
                                                                                    transform:
                                                                                        'scale(1)',
                                                                                },
                                                                            '50%': {
                                                                                opacity: 0.55,
                                                                                transform:
                                                                                    'scale(0.96)',
                                                                            },
                                                                        },
                                                                    // Respeita quem desativou animacoes no sistema.
                                                                    '@media (prefers-reduced-motion: reduce)':
                                                                        {
                                                                            animation:
                                                                                'none',
                                                                        },
                                                                }),
                                                            }}
                                                        />
                                                    </Tooltip>

                                                    {/* Divergencia: o aparelho
                                                        esta rodando, mas o
                                                        experimento dele nao
                                                        entrou nesta analise.
                                                        Silenciar isso ja custou
                                                        um ensaio inteiro. */}
                                                    {semVinculo > 0 && (
                                                        <Tooltip
                                                            title={`${semVinculo} dispositivo(s) desta análise estão rodando um experimento que NÃO foi vinculado a ela. O ensaio pode estar sendo gravado em outra análise — confira antes de calcular.`}
                                                        >
                                                            <Chip
                                                                icon={
                                                                    <Lucide.TriangleAlert
                                                                        size={
                                                                            14
                                                                        }
                                                                    />
                                                                }
                                                                label={`${semVinculo} sem vínculo`}
                                                                size="small"
                                                                color="error"
                                                                sx={{
                                                                    mt: 0.75,
                                                                    ml: 0.75,
                                                                    height: {
                                                                        xs: 26,
                                                                        sm: 22,
                                                                    },
                                                                    fontSize: {
                                                                        xs: '0.75rem',
                                                                        sm: '0.7rem',
                                                                    },
                                                                    fontWeight: 700,
                                                                    '& .MuiChip-icon':
                                                                        {
                                                                            ml: 0.75,
                                                                            color: 'inherit',
                                                                        },
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>

                                                <Chip
                                                    label={a?.status || '—'}
                                                    color={stColor}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        height: 24,
                                                    }}
                                                />
                                            </Stack>

                                            <Divider sx={{ mb: 2 }} />

                                            {mostrarEficiencia(a) ? (
                                                // SANEAMENTO: o que importa e a conformidade da
                                                // estacao, nao o protocolo (que e sempre o mesmo).
                                                <EficienciaResumo
                                                    variant="card"
                                                    eficiencia={a?.eficienciaEcoli}
                                                    gmAfluente={a?.gmEcoliAfluente}
                                                    gmEfluente={a?.gmEcoliEfluente}
                                                    naoDetectado={
                                                        a?.efluenteNaoDetectado === true
                                                    }
                                                    invalida={a?.eficienciaInvalida === true}
                                                    motivo={a?.eficienciaMotivo}
                                                    estado={estadoResultado(a)}
                                                    limite={estacao.limite}
                                                    buffer={estacao.buffer}
                                                />
                                            ) : (
                                                <Stack spacing={1}>
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        spacing={1}
                                                    >
                                                        <Lucide.ClipboardList
                                                            size={18}
                                                            color={theme.palette.action.active}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            noWrap
                                                            sx={{ flex: 1 }}
                                                        >
                                                            {resolveProtocoloLabel(a)}
                                                        </Typography>
                                                    </Stack>
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        spacing={1}
                                                    >
                                                        <Lucide.FlaskConical
                                                            size={18}
                                                            color={theme.palette.action.active}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            noWrap
                                                            sx={{ flex: 1 }}
                                                        >
                                                            {resolveAnaliseLabel(a)}
                                                        </Typography>
                                                    </Stack>

                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            mt: 1,
                                                            display: 'block',
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        {totalTorres === 1
                                                            ? `${vinculadasTorres} de 1 dispositivo vinculado`
                                                            : `${vinculadasTorres} de ${totalTorres} dispositivos vinculados`}
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </CardContent>
                                    </CardActionArea>

                                    <CardActions
                                        sx={{
                                            justifyContent: 'flex-end',
                                            bgcolor: 'background.paper',
                                            borderTop: '1px solid',
                                            borderColor: 'divider',
                                            // Mobile: mais respiro e alvos maiores (uso com o dedo).
                                            px: { xs: 1.5, sm: 1 },
                                            py: { xs: 1.25, sm: 1 },
                                            gap: { xs: 0.5, sm: 0 },
                                        }}
                                    >
                                        {showDeleted ? (
                                            <Button
                                                size={isMobile ? 'medium' : 'small'}
                                                variant="contained"
                                                color="success"
                                                disableElevation
                                                startIcon={
                                                    <Lucide.RotateCcw
                                                        size={isMobile ? 18 : 16}
                                                    />
                                                }
                                                onClick={(e) =>
                                                    handleRestore(a, e)
                                                }
                                                disabled={
                                                    restoringId ===
                                                    (a?.id || a?._id)
                                                }
                                                sx={{ borderRadius: 2 }}
                                            >
                                                {restoringId ===
                                                (a?.id || a?._id)
                                                    ? 'Restaurando…'
                                                    : 'Restaurar'}
                                            </Button>
                                        ) : (
                                            <>
                                                {/* Comentários: ao contrário do editar,
                                                    vale em QUALQUER status — é durante o
                                                    ensaio que aparece o que registrar. */}
                                                <Tooltip title="Comentários da análise">
                                                    <IconButton
                                                        size={
                                                            isMobile
                                                                ? 'large'
                                                                : 'small'
                                                        }
                                                        onClick={(e) =>
                                                            handleAbrirComentarios(
                                                                a,
                                                                e
                                                            )
                                                        }
                                                    >
                                                        <Badge
                                                            badgeContent={
                                                                a?.comentarios
                                                                    ?.length || 0
                                                            }
                                                            color="primary"
                                                            invisible={
                                                                !a?.comentarios
                                                                    ?.length
                                                            }
                                                        >
                                                            <Lucide.MessageSquare
                                                                size={
                                                                    isMobile
                                                                        ? 22
                                                                        : 18
                                                                }
                                                            />
                                                        </Badge>
                                                    </IconButton>
                                                </Tooltip>
                                                {podeEditar(a) && (
                                                    <Tooltip title="Editar Análise">
                                                        <IconButton
                                                            size={
                                                                isMobile
                                                                    ? 'large'
                                                                    : 'small'
                                                            }
                                                            onClick={(e) =>
                                                                handleOpenEdit(
                                                                    a,
                                                                    e
                                                                )
                                                            }
                                                        >
                                                            <Lucide.Pencil
                                                                size={
                                                                    isMobile
                                                                        ? 22
                                                                        : 18
                                                                }
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip
                                                    title={
                                                        podeExcluir(a)
                                                            ? 'Excluir Análise'
                                                            : 'Não é possível excluir durante o processamento/cálculo'
                                                    }
                                                >
                                                    <span>
                                                        <IconButton
                                                            size={
                                                                isMobile
                                                                    ? 'large'
                                                                    : 'small'
                                                            }
                                                            color="error"
                                                            disabled={
                                                                !podeExcluir(a)
                                                            }
                                                            onClick={(e) =>
                                                                handleAskDelete(
                                                                    a,
                                                                    e
                                                                )
                                                            }
                                                        >
                                                            <Lucide.Trash2
                                                                size={
                                                                    isMobile
                                                                        ? 22
                                                                        : 18
                                                                }
                                                            />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </>
                                        )}
                                        {!showDeleted && podeCalcular(a) && (
                                            <Tooltip title="Calcular resultado da análise">
                                                <Button
                                                    size={
                                                        isMobile
                                                            ? 'medium'
                                                            : 'small'
                                                    }
                                                    variant="contained"
                                                    color="primary"
                                                    disableElevation
                                                    startIcon={
                                                        <Lucide.Send
                                                            size={
                                                                isMobile ? 18 : 16
                                                            }
                                                        />
                                                    }
                                                    onClick={(e) =>
                                                        handleCalcularFromCard(
                                                            a,
                                                            e
                                                        )
                                                    }
                                                    sx={{
                                                        ml: 1,
                                                        borderRadius: 2,
                                                    }}
                                                >
                                                    Calcular
                                                </Button>
                                            </Tooltip>
                                        )}
                                        {!showDeleted &&
                                            analiseEmAtividade(a) && (
                                                <Tooltip title="Aguarde: a análise ainda não pode ser calculada">
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        color="info"
                                                        icon={
                                                            <Lucide.Clock
                                                                size={14}
                                                            />
                                                        }
                                                        label={estadoAtividadeLabel(
                                                            a
                                                        )}
                                                        sx={{
                                                            ml: 1,
                                                            height: 26,
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                        {/* Apoio, nao a acao principal: quem
                                            manda no card e o Calcular quando ele
                                            existe. Preenchido, este botao ficava
                                            com a MESMA cor do FAB de criar, no
                                            mesmo canto — dois "azuis cheios"
                                            disputando o olho. */}
                                        <Button
                                            size={isMobile ? 'medium' : 'small'}
                                            variant="outlined"
                                            onClick={() =>
                                                handleOpenDetalhes(a)
                                            }
                                            sx={{ ml: 1, borderRadius: 2 }}
                                        >
                                            Detalhes
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        );
                    })
                )}
            </Grid>

            <PaginacaoFooter
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={setItemsPerPage}
                itemsPerPageOptions={[5, 10, 20, 50]}
                loading={loading}
            />

            <AmostraFormModal
                open={openForm}
                onClose={handleCloseForm}
                onSubmit={handleSubmitForm}
                initialValue={editing}
                mode={editing ? 'edit' : 'create'}
            />
            <AnaliseAutoCreateModal
                open={openAuto}
                onClose={() => setOpenAuto(false)}
                onCreate={handleCreateAuto}
            />
            <AmostraMockModal
                open={openMock}
                onClose={handleCloseMock}
                onSubmit={handleSubmitMock}
            />
            <AnaliseDetalhesModal
                open={openDetalhes}
                onClose={handleCloseDetalhes}
                amostra={detalhesTarget}
                loading={detalhesLoading}
                detalhes={detalhesData}
                error={detalhesError}
                limite={estacao.limite}
                buffer={estacao.buffer}
                mostrarEficiencia={isSaneamentoClient}
                onRefresh={async () => {
                    // Só recarrega a LISTA. O modal de detalhes se atualiza sozinho
                    // (efeito de abertura + polling ao vivo), então não reabrimos aqui
                    // — reabrir brigaria com o onClose do "Calcular".
                    await loadAmostras();
                }}
            />

            <ComentariosAnaliseDialog
                open={!!amostraComentarios}
                onClose={() => setComentariosId(null)}
                identificacao={amostraComentarios?.identificacao}
                comentarios={amostraComentarios?.comentarios}
                onAdicionar={handleAdicionarComentario}
                // Análise excluída não aceita comentário (o servidor responde 409):
                // na lixeira o histórico fica só de leitura.
                somenteLeitura={showDeleted}
            />

            <EteRelatorioModal
                open={openEte}
                onClose={() => setOpenEte(false)}
                amostras={visibleAmostras}
            />

            <Dialog
                open={openNormalize}
                onClose={normalizing ? undefined : handleCloseNormalize}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Normalizar Análises Por Dia</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 0.5 }}>
                        <FormControl fullWidth disabled={normalizing}>
                            <InputLabel>Escopo</InputLabel>
                            <Select
                                value={normalizeForm.escopo}
                                label="Escopo"
                                onChange={(e) =>
                                    setNormalizeForm((prev) => ({
                                        ...prev,
                                        escopo: e.target.value,
                                    }))
                                }
                            >
                                <MenuItem value="dia">Apenas um dia</MenuItem>
                                <MenuItem value="periodo">
                                    Periodo de datas
                                </MenuItem>
                                <MenuItem value="todos">
                                    Todos os experimentos
                                </MenuItem>
                            </Select>
                        </FormControl>

                        {normalizeForm.escopo === 'dia' && (
                            <TextField
                                label="Data de referencia"
                                type="date"
                                value={normalizeForm.dataReferencia}
                                onChange={(e) =>
                                    setNormalizeForm((prev) => ({
                                        ...prev,
                                        dataReferencia: e.target.value,
                                    }))
                                }
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                disabled={normalizing}
                            />
                        )}

                        {normalizeForm.escopo === 'periodo' && (
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Data inicio"
                                        type="date"
                                        value={normalizeForm.dataInicio}
                                        onChange={(e) =>
                                            setNormalizeForm((prev) => ({
                                                ...prev,
                                                dataInicio: e.target.value,
                                            }))
                                        }
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        disabled={normalizing}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Data fim"
                                        type="date"
                                        value={normalizeForm.dataFim}
                                        onChange={(e) =>
                                            setNormalizeForm((prev) => ({
                                                ...prev,
                                                dataFim: e.target.value,
                                            }))
                                        }
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        disabled={normalizing}
                                    />
                                </Grid>
                            </Grid>
                        )}

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Janela (minutos)"
                                    type="number"
                                    value={normalizeForm.janelaMinutos}
                                    onChange={(e) =>
                                        setNormalizeForm((prev) => ({
                                            ...prev,
                                            janelaMinutos: e.target.value,
                                        }))
                                    }
                                    inputProps={{ min: 5, max: 720 }}
                                    fullWidth
                                    disabled={normalizing}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Min. experimentos"
                                    type="number"
                                    value={normalizeForm.minimoExperimentos}
                                    onChange={(e) =>
                                        setNormalizeForm((prev) => ({
                                            ...prev,
                                            minimoExperimentos: e.target.value,
                                        }))
                                    }
                                    inputProps={{ min: 1, max: 20 }}
                                    fullWidth
                                    disabled={normalizing}
                                />
                            </Grid>
                        </Grid>

                        <FormControl fullWidth disabled={normalizing}>
                            <InputLabel>Status do experimento</InputLabel>
                            <Select
                                value={normalizeForm.statusExperimento}
                                label="Status do experimento"
                                onChange={(e) =>
                                    setNormalizeForm((prev) => ({
                                        ...prev,
                                        statusExperimento: e.target.value,
                                    }))
                                }
                            >
                                <MenuItem value="completed">completed</MenuItem>
                                <MenuItem value="">todos</MenuItem>
                            </Select>
                        </FormControl>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Temp. análise (opcional)"
                                    type="number"
                                    value={normalizeForm.temperaturaAmostra}
                                    onChange={(e) =>
                                        setNormalizeForm((prev) => ({
                                            ...prev,
                                            temperaturaAmostra: e.target.value,
                                        }))
                                    }
                                    fullWidth
                                    disabled={normalizing}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Latitude (opcional)"
                                    type="number"
                                    value={normalizeForm.latitude}
                                    onChange={(e) =>
                                        setNormalizeForm((prev) => ({
                                            ...prev,
                                            latitude: e.target.value,
                                        }))
                                    }
                                    fullWidth
                                    disabled={normalizing}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Longitude (opcional)"
                                    type="number"
                                    value={normalizeForm.longitude}
                                    onChange={(e) =>
                                        setNormalizeForm((prev) => ({
                                            ...prev,
                                            longitude: e.target.value,
                                        }))
                                    }
                                    fullWidth
                                    disabled={normalizing}
                                />
                            </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                variant="text"
                                size="small"
                                onClick={() =>
                                    prefillNormalizeLocationFromGps(true)
                                }
                                disabled={normalizing || normalizeLocLoading}
                            >
                                Usar GPS atual
                            </Button>
                            {(normalizing || normalizeLocLoading) && (
                                <CircularProgress size={16} />
                            )}
                            {normalizeLocMessage && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {normalizeLocMessage}
                                </Typography>
                            )}
                        </Stack>

                        <Typography variant="caption" color="text.secondary">
                            Primeiro use <b>Simular</b> para ver como as
                            análises serão criadas. Depois use{' '}
                            <b>Criar Análises</b>.
                        </Typography>

                        {normalizePreview && (
                            <Paper
                                variant="outlined"
                                sx={{ p: 2, borderRadius: 2 }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={700}
                                    gutterBottom
                                >
                                    Prévia da Normalização
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    Escopo: {normalizePreview?.escopo || 'dia'}{' '}
                                    |{' '}
                                    {normalizePreview?.periodoDescricao || '-'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    Grupos detectados:{' '}
                                    {normalizePreview?.gruposDetectados || 0} |
                                    Criados/Aptos:{' '}
                                    {normalizePreview?.gruposCriados || 0} |
                                    Ignorados:{' '}
                                    {normalizePreview?.gruposIgnorados || 0}
                                </Typography>
                                <Divider sx={{ my: 1.5 }} />
                                <Stack
                                    spacing={1.25}
                                    sx={{ maxHeight: 220, overflow: 'auto' }}
                                >
                                    {(normalizePreview?.grupos || []).map(
                                        (g, idx) => (
                                            <Box
                                                key={`${g?.amostraId || g?.identificacao || 'grupo'}-${idx}`}
                                                sx={{
                                                    p: 1.25,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1.5,
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    flexWrap="wrap"
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                    >
                                                        {resolveProtocoloLabel(
                                                            g
                                                        )}{' '}
                                                        /{' '}
                                                        {resolveAnaliseLabel(g)}
                                                    </Typography>
                                                    <Chip
                                                        size="small"
                                                        label={`${g?.quantidadeExperimentos || 0} experimento(s)`}
                                                        color={
                                                            (g?.quantidadeExperimentos ||
                                                                0) >=
                                                            Number(
                                                                normalizeForm.minimoExperimentos ||
                                                                    2
                                                            )
                                                                ? 'success'
                                                                : 'default'
                                                        }
                                                        variant="outlined"
                                                    />
                                                    {g?.identificacao && (
                                                        <Chip
                                                            size="small"
                                                            label={
                                                                g.identificacao
                                                            }
                                                            color="info"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    {g?.amostraId && (
                                                        <Chip
                                                            size="small"
                                                            label={`Análise: ${g.amostraId}`}
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Stack>
                                                {g?.experimentIds?.length >
                                                    0 && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        display="block"
                                                        sx={{ mt: 0.5 }}
                                                    >
                                                        Experimentos:{' '}
                                                        {g.experimentIds.join(
                                                            ', '
                                                        )}
                                                    </Typography>
                                                )}
                                                {g?.serialNumbers?.length >
                                                    0 && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        display="block"
                                                        sx={{ mt: 0.5 }}
                                                    >
                                                        Seriais:{' '}
                                                        {g.serialNumbers.join(
                                                            ', '
                                                        )}
                                                    </Typography>
                                                )}
                                                {g?.motivo && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        display="block"
                                                        sx={{ mt: 0.5 }}
                                                    >
                                                        {g.motivo}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )
                                    )}
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseNormalize}
                        disabled={normalizing}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => handleSubmitNormalize(true)}
                        disabled={normalizing || !canRunNormalize}
                        variant="outlined"
                    >
                        {normalizing ? (
                            <CircularProgress size={18} />
                        ) : (
                            'Simular'
                        )}
                    </Button>
                    <Button
                        onClick={() => handleSubmitNormalize(false)}
                        disabled={normalizing || !canRunNormalize}
                        variant="contained"
                    >
                        {normalizing ? (
                            <CircularProgress size={18} />
                        ) : (
                            'Criar Análises'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmDeleteOpen}
                onClose={deleting ? undefined : handleCloseDelete}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Excluir Análise</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        A análise{' '}
                        <strong>{deleteTarget?.identificacao || '—'}</strong>{' '}
                        será movida para a lixeira. Você poderá restaurá-la
                        depois. Informe o motivo da exclusão.
                    </Typography>

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Motivo *</InputLabel>
                        <Select
                            label="Motivo *"
                            value={deleteMotivo}
                            onChange={(e) => setDeleteMotivo(e.target.value)}
                        >
                            {MOTIVOS_EXCLUSAO.map((m) => (
                                <MenuItem key={m} value={m}>
                                    {m}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        label={
                            deleteMotivo === 'Outro'
                                ? 'Descreva o motivo *'
                                : 'Observação (opcional)'
                        }
                        placeholder={
                            deleteMotivo === 'Outro'
                                ? 'Descreva o motivo da exclusão'
                                : 'Detalhe se necessário'
                        }
                        value={deleteObs}
                        onChange={(e) => setDeleteObs(e.target.value)}
                        error={deleteMotivo === 'Outro' && !deleteObs.trim()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseDelete}
                        disabled={deleting}
                        variant="text"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        disabled={deleting || !deleteMotivoValido}
                        variant="contained"
                        color="error"
                    >
                        {deleting ? <CircularProgress size={18} /> : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Acoes flutuantes (mobile) ─────────────────────────────────
                Canto inferior direito = zona de alcance do polegar. A acao
                principal (Nova Analise) fica embaixo, mais perto do dedo. */}
            {isMobile && (
                <Box
                    sx={{
                        position: 'fixed',
                        right: 16,
                        bottom: 24,
                        zIndex: (t) => t.zIndex.speedDial,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <Fab
                        size="small"
                        color="default"
                        aria-label="Atualizar lista"
                        onClick={() => loadAmostras()}
                        disabled={loading}
                        sx={{
                            // `background.paper` no dark e #161B22 sobre um fundo
                            // #0D1117 — o botao praticamente sumia. Um FAB precisa
                            // ler como superficie FLUTUANTE, entao ele fica claro
                            // nos dois temas, com o icone escuro.
                            bgcolor: 'common.white',
                            color: 'grey.900',
                            boxShadow: 3,
                            '&:hover': { bgcolor: 'grey.200' },
                        }}
                    >
                        <Lucide.RefreshCw size={20} />
                    </Fab>

                    {!showDeleted && (
                        <Fab
                            color="primary"
                            aria-label="Nova Análise"
                            onClick={handleOpenCreate}
                            sx={{ boxShadow: 6 }}
                        >
                            <Lucide.Plus size={26} />
                        </Fab>
                    )}
                </Box>
            )}

            <SnackbarAlert
                open={pageSnackbar.open}
                onClose={() =>
                    setPageSnackbar((prev) => ({ ...prev, open: false }))
                }
                message={pageSnackbar.message}
                severity={pageSnackbar.severity}
            />
        </Box>
    );
};

export default AmostraPage;
